import { useCallback, useRef, useState } from 'react';
import './InformedConsents.scss';
import moment from 'moment';
import { newToast } from '../../components';
import DataGrid, { Column, Paging, Scrolling, Selection, Sorting } from 'devextreme-react/data-grid';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { Button, LoadIndicator, LoadPanel, Tooltip, ScrollView } from 'devextreme-react';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import Form, { Item as FormItem, Label } from 'devextreme-react/form';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../../contexts/hooks';
import { useScreenSize } from '../../utils/media-query';
import { useApiInformedConsents } from '../../api/api.informedConsents';
import { useApiOrders } from '../../api/api.orders';
import { ConvertDataOrder, ConvertDataPatient } from '../../utils/functions';
import { RightPanelControlled } from '../../components/utils/right-panel-controlled/RighPanel';
import { OrderInfo, PatientInfo } from '../../components/library/order-patient-info';
import StepForm from '../../components/library/info-order/OrderModules/Functions/StepForm';
import { filter, resButtonRefresh } from '../../images/icons';

// Estados del consentimiento (Roc_Estado). Coinciden con EConsentimientosState del backend.
const ESTADOS = [
  { id: 0, name: 'Pendiente', color: '#9e9e9e' },
  { id: 1, name: 'Diligenciado', color: '#65ABF2' },
  { id: 2, name: 'Firmado', color: '#F6C024' },
  { id: 3, name: 'Autorizado', color: '#49A54A' },
];
const estadoInfo = (id: number) => ESTADOS.find((e) => e.id === id) || { id, name: `Estado ${id}`, color: '#9e9e9e' };
// Un consentimiento es descargable como documento legal a partir de "Firmado".
const ESTADO_FIRMADO = 2;

const Index = () => {
  const { buscarConsentimientosMasivo, descargarConsentimientoPdf, getConsentimientoImportadoPdf } = useApiInformedConsents();
  const { getHeaderOrderById, findOrder } = useApiOrders();
  const frmRef = useRef<Form>(null);

  const { isSmall, isXSmall, isMedium, isLarge } = useScreenSize();
  const Prm_Consentimientos_R = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord_Consentimientos_R, shallowEqual) ?? false;

  const [loading, setLoading] = useState<boolean>(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [loadingFindOrder, setLoadingFindOrder] = useState<boolean>(false);
  const [modalFiltersVisible, setModalFiltersVisible] = useState<boolean>(true); // abre el modal de filtros al entrar
  const [appliedFiltersText, setAppliedFiltersText] = useState<string>('');
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [gridData, setGridData] = useState<any[]>([]);

  // Panel derecho de información de la orden (igual que en códigos de barras).
  const isPanelPinned = !isSmall && !isXSmall;
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [currentPatient, setCurrentPatient] = useState<any>(null);
  const [loadingPanel, setLoadingPanel] = useState<boolean>(false);

  // Modal de datos clínicos (solo lectura).
  const [datosModal, setDatosModal] = useState<{ visible: boolean; template: any[] | null; values: any | null; title: string }>({ visible: false, template: null, values: null, title: '' });

  // Datos del formulario de filtros (ref para que no se reinicie en cada render).
  const FieldsToFiltersRef = useRef<any>({
    Fechas: [new Date().toISOString(), new Date().toISOString()],
    Documento: '',
    Estados: [],
  });
  const appliedFiltersRef = useRef<any>(null);

  const fetchData = useCallback(
    async (filtro: any) => {
      setLoading(true);
      try {
        const res = await buscarConsentimientosMasivo(filtro);
        const dataArray = Array.isArray(res) ? res : [];
        setTotalRecords(dataArray.length);
        setGridData(dataArray);
      } catch {
        console.error('Error de carga de datos');
      } finally {
        setLoading(false);
      }
    },
    [buscarConsentimientosMasivo]
  );

  const handleModalToggleFilter = useCallback(() => {
    setModalFiltersVisible((v) => !v);
  }, []);

  const handleRefresh = useCallback(() => {
    if (appliedFiltersRef.current) fetchData(appliedFiltersRef.current);
  }, [fetchData]);

  const handleConfirm = useCallback(() => {
    if (!frmRef.current?.instance) return;
    const fechas = frmRef.current.instance.getEditor('Fechas')?.option('value');
    const documento = (frmRef.current.instance.getEditor('Documento')?.option('value') || '').trim();
    const estados = frmRef.current.instance.getEditor('Estados')?.option('value') || [];

    const hasFechas = fechas && Array.isArray(fechas) && fechas.length === 2 && fechas[0] && fechas[1];
    const hasDocumento = documento !== '';

    // Debe haber al menos un criterio acotante (fechas o documento) para no traer toda la base.
    if (!hasFechas && !hasDocumento) {
      const fechasEditor = frmRef.current.instance.getEditor('Fechas');
      const docEditor = frmRef.current.instance.getEditor('Documento');
      if (fechasEditor) fechasEditor.option('isValid', false);
      if (docEditor) docEditor.option('isValid', false);
      return;
    }

    const filtro: any = {
      Documento: hasDocumento ? documento : null,
      FechaDesde: hasFechas ? moment(fechas[0]).format('YYYY-MM-DDT00:00:00') : null,
      FechaHasta: hasFechas ? moment(fechas[1]).format('YYYY-MM-DDT23:59:59') : null,
      Estados: estados.length > 0 ? estados : null,
    };
    appliedFiltersRef.current = filtro;

    // Texto de filtros aplicados (para la barra superior).
    const parts: string[] = [];
    if (hasDocumento) parts.push(`Doc: ${documento}`);
    if (hasFechas) parts.push(`${moment(fechas[0]).format('DD/MM/YYYY')} - ${moment(fechas[1]).format('DD/MM/YYYY')}`);
    if (estados.length > 0) parts.push(`Estado: ${estados.map((e: number) => estadoInfo(e).name).join(', ')}`);
    setAppliedFiltersText(parts.join(' | '));

    setModalFiltersVisible(false);
    fetchData(filtro);
  }, [fetchData]);

  const handleClearFilters = useCallback(() => {
    const today = new Date();
    frmRef.current?.instance.getEditor('Fechas')?.option('value', [today, today]);
    frmRef.current?.instance.getEditor('Documento')?.option('value', '');
    frmRef.current?.instance.getEditor('Estados')?.option('value', []);
  }, []);

  //#region PANEL DERECHO (info de orden)
  const handleOrderClick = useCallback(
    async (orderId: number) => {
      if (!orderId) return;
      if (!isPanelPinned) setIsPanelOpen(true);
      setLoadingPanel(true);
      try {
        const res = await getHeaderOrderById(orderId);
        if (res) {
          setCurrentOrder(ConvertDataOrder(res.Orden));
          setCurrentPatient(ConvertDataPatient(res.Paciente));
        } else {
          setCurrentOrder(null);
          setCurrentPatient(null);
          newToast('WARNING', 'No se encontró información de la orden', 3);
        }
      } catch {
        newToast('ERROR', 'Error al cargar los datos de la orden', 3);
        setCurrentOrder(null);
        setCurrentPatient(null);
      } finally {
        setLoadingPanel(false);
      }
    },
    [getHeaderOrderById, isPanelPinned]
  );

  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
    setCurrentOrder(null);
    setCurrentPatient(null);
  }, []);

  const handleTogglePanel = useCallback(() => {
    if (isPanelPinned) return;
    setIsPanelOpen((prev) => !prev);
  }, [isPanelPinned]);

  const handleClickViewOrder = useCallback(() => {
    if (currentOrder) findOrder(undefined, currentOrder.Ord_Id, setLoadingFindOrder, true);
  }, [currentOrder, findOrder]);
  //#endregion

  //#region DATOS CLÍNICOS / DESCARGA
  const openDatosClinicos = useCallback((row: any) => {
    let template: any[] | null = null;
    let values: any = null;
    try {
      template = row?.PlantillaCampos ? (typeof row.PlantillaCampos === 'string' ? JSON.parse(row.PlantillaCampos) : row.PlantillaCampos) : null;
    } catch {
      template = null;
    }
    try {
      values = row?.PlantillaValores ? (typeof row.PlantillaValores === 'string' ? JSON.parse(row.PlantillaValores) : row.PlantillaValores) : null;
    } catch {
      values = null;
    }
    setDatosModal({ visible: true, template, values, title: `Datos solicitados - ${row.NombreConsentimiento || ''}` });
  }, []);

  const handleDownload = useCallback(
    async (row: any) => {
      if (!Prm_Consentimientos_R) {
        newToast('INFO', 'No cuenta con permisos para consultar consentimientos', 5);
        return;
      }
      setDownloadingId(row.RocId);
      try {
        if (row.EsImportado) {
          // Recibido de otro laboratorio: el PDF vive en el hub, se abre con una URL prefirmada.
          const url = await getConsentimientoImportadoPdf(row.Uuid);
          if (url) window.open(url, '_blank', 'noopener');
        } else {
          if (row.Estado < ESTADO_FIRMADO) {
            newToast('INFO', 'El consentimiento aún no está firmado; su PDF estará disponible al firmarse.', 5);
            return;
          }
          const fileName = `Consentimiento-${row.NumeroOrden || row.IdOrden}-${(row.NombreConsentimiento || '').replace(/\s+/g, '')}.pdf`;
          await descargarConsentimientoPdf(row.IdOrden, row.IdCst, fileName);
        }
      } finally {
        setDownloadingId(null);
      }
    },
    [Prm_Consentimientos_R, descargarConsentimientoPdf, getConsentimientoImportadoPdf]
  );
  //#endregion

  //#region CELL RENDERERS
  const renderOrderCell = useCallback(
    (cellData: any) => (
      <div className='informed-consents-order-cell' onClick={() => handleOrderClick(cellData.data?.IdOrden)}>
        {cellData.data?.NumeroOrden ?? cellData.data?.IdOrden}
      </div>
    ),
    [handleOrderClick]
  );

  const renderConsentCell = useCallback((cellData: any) => {
    const row = cellData.data;
    return (
      <div>
        <div id={`ic-name-${row?.RocId}`} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row?.NombreConsentimiento}
        </div>
        {row?.EsImportado && row?.Emisor && <div style={{ fontSize: 11, color: '#7E57C2' }}>De: {row.Emisor}</div>}
      </div>
    );
  }, []);

  const renderEstudiosCell = useCallback((cellData: any) => {
    const estudios: any[] = cellData.data?.Estudios || [];
    if (!estudios.length) return <span style={{ color: '#9e9e9e' }}>General</span>;
    const txt = estudios.map((e) => (e.Nombre ? `${e.Codigo} - ${e.Nombre}` : e.Codigo)).join(', ');
    return <span title={txt}>{txt}</span>;
  }, []);

  const renderEstadoCell = useCallback((cellData: any) => {
    const row = cellData.data;
    if (row?.EsImportado) return <span className='ic-badge' style={{ backgroundColor: '#7E57C2' }}>Recibido</span>;
    const info = estadoInfo(row?.Estado);
    return <span className='ic-badge' style={{ backgroundColor: info.color }}>{info.name}</span>;
  }, []);

  const renderAccionCell = useCallback(
    (cellData: any) => {
      const row = cellData.data;
      const firmado = row.Estado >= ESTADO_FIRMADO;
      const isDownloading = downloadingId === row.RocId;
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
          <i
            id={`ic-data-${row.RocId}`}
            className='dx-icon dx-icon-textdocument'
            style={{ cursor: 'pointer', fontSize: 18 }}
            onClick={() => openDatosClinicos(row)}
          />
          <i
            id={`ic-download-${row.RocId}`}
            className='dx-icon dx-icon-pdffile'
            style={{ cursor: firmado ? 'pointer' : 'default', fontSize: 18, opacity: firmado ? 1 : 0.35 }}
            onClick={() => firmado && !isDownloading && handleDownload(row)}
          />
        </div>
      );
    },
    [downloadingId, handleDownload, openDatosClinicos]
  );
  //#endregion

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper-contact-list'>
        <div className='view-wrapper-informed-consents' style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <div className='toolbar-informed-consents'>
            <Toolbar>
              <Item location='before' visible={isLarge || isMedium}>
                <div className='grid-header'>Consentimientos informados</div>
              </Item>
              {appliedFiltersText && (
                <Item location='before' locateInMenu='auto'>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '0 8px' }}>
                    <span style={{ fontSize: '12px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '500px' }}>{appliedFiltersText}</span>
                  </div>
                </Item>
              )}
              <Item location='after' locateInMenu='auto'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 10px', minWidth: 'fit-content' }}>
                  <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>
                    Registros: <strong>{totalRecords}</strong>
                  </span>
                </div>
              </Item>
              <Item location='after' locateInMenu='auto'>
                <div className='separator' />
              </Item>
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Tooltip target='.filter-button-ic' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                  Filtrar consentimientos
                </Tooltip>
                <Button className='filter-button-ic' icon={filter} text='Filtrar' stylingMode='text' onClick={handleModalToggleFilter} />
              </Item>
              <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                <Button id='RefrescarConsentimientos' icon={resButtonRefresh} text='Actualizar' stylingMode='text' onClick={handleRefresh} />
                <Tooltip target={'#RefrescarConsentimientos'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Refrescar datos
                </Tooltip>
              </Item>
            </Toolbar>
          </div>

          <div style={{ flex: 1, overflow: 'auto', paddingBottom: '40px' }}>
            <DataGrid hoverStateEnabled className='gridResult' height={'100%'} dataSource={gridData} width={'100%'} keyExpr='RocId' repaintChangesOnly={false} showColumnLines={true} cacheEnabled={false}>
              <Selection mode='multiple' selectAllMode='page' showCheckBoxesMode='always' />
              <Scrolling mode='standard' showScrollbar='always' useNative={true} scrollByContent={true} />
              <Sorting mode='multiple' />
              <Paging enabled={false} />
              <Column dataField='NumeroOrden' caption='ORDEN' width={100} cellRender={renderOrderCell} />
              <Column dataField='Documento' caption='DOCUMENTO' width={130} />
              <Column dataField='Paciente' caption='PACIENTE' />
              <Column dataField='NombreConsentimiento' caption='CONSENTIMIENTO' width={200} cellRender={renderConsentCell} />
              <Column dataField='Estudios' caption='ESTUDIOS' allowSorting={false} cellRender={renderEstudiosCell} />
              <Column dataField='Estado' caption='ESTADO' width={130} alignment='center' cellRender={renderEstadoCell} />
              <Column dataField='FechaFirma' caption='FECHA FIRMA' dataType='date' format='dd/MM/yyyy' width={120} />
              <Column caption='ACCIÓN' width={110} alignment='center' allowSorting={false} cellRender={renderAccionCell} />
            </DataGrid>
          </div>

          {/* Tooltips FUERA de la grilla (apuntando por id): al renderizarlos dentro del cellRender
              descuadran/mueven los íconos de acción al pasar el cursor. */}
          {gridData.map((row: any) => (
            <span key={`tips-${row.RocId}`}>
              <Tooltip target={`#ic-name-${row.RocId}`} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                {row.NombreConsentimiento}
              </Tooltip>
              <Tooltip target={`#ic-data-${row.RocId}`} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Ver datos clínicos
              </Tooltip>
              <Tooltip target={`#ic-download-${row.RocId}`} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                {row.EsImportado ? 'Abrir PDF (recibido)' : row.Estado >= ESTADO_FIRMADO ? 'Descargar PDF' : 'Disponible al firmar'}
              </Tooltip>
            </span>
          ))}
        </div>

        {/* Panel derecho con la información de la orden (igual que en Códigos de barras) */}
        <RightPanelControlled isVisible={isPanelPinned || isPanelOpen} isPinned={isPanelPinned} onClose={handleClosePanel} includePin={true} title='Consulta de Orden'>
          {loadingPanel ? (
            <div style={{ padding: '30px', textAlign: 'center' }}>
              <LoadIndicator visible={true} />
              <div style={{ marginTop: '15px', color: '#666' }}>Cargando información...</div>
            </div>
          ) : currentOrder ? (
            <>
              <OrderInfo orderData={currentOrder} />
              <PatientInfo patientData={currentPatient} />
              <div style={{ padding: '15px', borderTop: '1px solid #e0e0e0' }}>
                <Button text='IR A LA ORDEN' type='default' stylingMode='contained' width='100%' onClick={handleClickViewOrder} disabled={!currentOrder || loadingFindOrder} />
              </div>
            </>
          ) : (
            <div style={{ padding: '30px', textAlign: 'center', color: '#999' }}>
              <div style={{ fontSize: '16px', marginBottom: '10px' }}>
                <i className='dx-icon-info' style={{ fontSize: '48px', color: '#ddd' }} />
              </div>
              <div>Haz clic en el número de orden para ver sus detalles</div>
            </div>
          )}
        </RightPanelControlled>

        {/* Modal de filtros */}
        <Popup title='Filtrar consentimientos' visible={modalFiltersVisible} showCloseButton={true} onHiding={() => setModalFiltersVisible(false)} fullScreen={isXSmall} width={isXSmall ? 360 : 560} wrapperAttr={{ class: 'form-popup' }} height={isXSmall ? '100%' : 'auto'} maxHeight={isXSmall ? '100%' : '80vh'}>
          <ScrollView height='100%' showScrollbar='onScroll'>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}>
              <div style={{ padding: '12px 6px 4px' }}>
                <Form ref={frmRef} formData={FieldsToFiltersRef.current} colCount={1}>
                  <FormItem dataField='Documento'>
                    <Label text='Documento del paciente' />
                  </FormItem>
                  <FormItem dataField='Fechas' editorType='dxDateRangeBox' editorOptions={{ displayFormat: 'shortdate', showClearButton: true }}>
                    <Label text='Fecha (creación)' />
                  </FormItem>
                  <FormItem dataField='Estados' editorType='dxTagBox' editorOptions={{ items: ESTADOS, valueExpr: 'id', displayExpr: 'name', showSelectionControls: true, showClearButton: true, placeholder: 'Todos los estados' }}>
                    <Label text='Estado' />
                  </FormItem>
                </Form>
              </div>
              <button type='submit' style={{ display: 'none' }} aria-hidden />
            </form>
          </ScrollView>
          <ToolbarItem toolbar='bottom' location='before'>
            <Button text='Limpiar' stylingMode='text' icon='clear' onClick={handleClearFilters} />
          </ToolbarItem>
          <ToolbarItem toolbar='bottom' location='after'>
            <div className='form-popup-buttons-container'>
              <Button text='Cancelar' stylingMode='contained' onClick={() => setModalFiltersVisible(false)} />
              <Button text='Buscar' disabled={loading} stylingMode='contained' type='default' onClick={handleConfirm} />
            </div>
          </ToolbarItem>
        </Popup>

        {/* Modal de datos clínicos (solo lectura) */}
        <Popup
          visible={datosModal.visible}
          onHiding={() => setDatosModal({ visible: false, template: null, values: null, title: '' })}
          showTitle={true}
          title={datosModal.title}
          width={720}
          height={'auto'}
          showCloseButton={true}
          fullScreen={isSmall || isXSmall}
          shading={true}>
          {datosModal.template && datosModal.template.length > 0 ? (
            <div style={{ padding: 8 }}>
              <StepForm template={datosModal.template} initialValues={datosModal.values} readOnly={true} onCancel={() => {}} onSubmit={async () => {}} />
            </div>
          ) : (
            <div style={{ padding: 16 }}>No hay datos para mostrar.</div>
          )}
        </Popup>
      </div>

      <LoadPanel visible={loading || loadingFindOrder} />
      <LoadPanel visible={downloadingId !== null} message='Descargando documento…' showIndicator={true} showPane={true} shading={true} shadingColor='rgba(0,0,0,0.25)' />
    </div>
  );
};

export default Index;
